/**
 * Блок «Перезвоны»: договорённости диспетчера, ещё не выданные карточками.
 *
 * Список — план на день («15:00 — Иван, обсудить списание»). В момент
 * договорённости пульт пикает и, если вкладка свёрнута, шлёт браузерное
 * уведомление; строка красится: «пора» — оранжевым, спустя 15 минут —
 * «просрочен» красным (callback-alarm.ts).
 *
 * С 06.09.2026 по строке можно позвонить: «Позвонить» забирает номер
 * карточкой (POST /desk/callbacks/:id/take — свой перезвон, не выдача из
 * общего пула, ADR-0006) и пульт сразу набирает. Не нажали — номер всё равно
 * придёт сам с ближайшей раздачей, как и раньше. В наблюдении за чужим
 * пультом кнопки нет: звонить нечем.
 *
 * Звук — короткий двойной тон Web Audio без аудиофайлов. Автоплей-политика:
 * хотя бы один клик по странице с момента загрузки разблокирует звук насовсем;
 * свежеоткрытая вкладка без единого клика пикнуть не сможет — останутся
 * подсветка строки и уведомление.
 *
 * Будильник заводится только по строкам, где время назначил человек
 * (scheduled): у строк, размеченных до появления выбора времени, dueAt — это
 * пауза круга, будить по ней значит врать про договорённость.
 */

import { useEffect, useRef, useState } from 'react';
import { BellRing, Phone } from 'lucide-react';
import type { DeskCallbacksResponse } from '@cc/shared';

import { formatPhone } from '../../shared/lib/format-phone.js';
import { formatWhenMsk } from '../../shared/lib/msk.js';
import { dueState, newlyDue, nextDueAt, timerDelayMs, type DueState } from './callback-alarm.js';

export type CallbackItem = DeskCallbacksResponse['items'][number];

export interface CallbacksPanelProps {
  items: CallbackItem[];
  /** Наблюдение за чужим пультом: список показываем, звук и уведомления — нет */
  withSound: boolean;
  /** Забрать номер карточкой и набрать; нет колбэка — кнопки нет (наблюдение) */
  onTake?: ((item: CallbackItem) => void) | undefined;
  /** Перезвон, который сейчас забирается: его кнопка показывает «Набираю…» */
  takingId?: string | null | undefined;
  /** Ошибка последней попытки забрать: что случилось и что делать */
  takeError?: string | undefined;
}

export function CallbacksPanel({
  items,
  withSound,
  onTake,
  takingId,
  takeError,
}: CallbacksPanelProps): React.JSX.Element | null {
  // Перерисовка каждые 30 секунд: «пора»/«просрочен» должны наступать сами,
  // без событий с сервера — время идёт и на молчащем пульте
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => {
      setNow(new Date());
    }, 30_000);
    return () => {
      clearInterval(id);
    };
  }, []);

  // По каждой договорённости пикаем один раз; ключ — перезвон плюс его время:
  // новая договорённость по тому же номеру пикает снова. Set в ref: писк — не
  // состояние экрана. Будим только по временам, назначенным человеком
  const alerted = useRef(new Set<string>());
  useEffect(() => {
    if (!withSound) return;
    const alarmable = items.filter((item) => item.scheduled);
    const due = newlyDue(
      alarmable.map((item) => ({ key: alarmKey(item), dueAt: new Date(item.dueAt) })),
      alerted.current,
      now,
    );
    if (due.length === 0) return;
    for (const key of due) alerted.current.add(key);
    beep();
    notify(alarmable.filter((item) => due.includes(alarmKey(item))));
  }, [items, now, withSound]);

  // Таймер точно на ближайший момент: интервала в 30 секунд мало, писк должен
  // прозвучать в саму минуту договорённости, а не до полуминуты позже
  useEffect(() => {
    if (!withSound) return;
    const next = nextDueAt(
      items.filter((item) => item.scheduled).map((item) => new Date(item.dueAt)),
      now,
    );
    if (next === null) return;
    const id = setTimeout(() => {
      setNow(new Date());
    }, timerDelayMs(next, new Date()));
    return () => {
      clearTimeout(id);
    };
  }, [items, now, withSound]);

  const [notifState, setNotifState] = useState<NotificationPermission | 'unsupported'>(() =>
    typeof Notification === 'undefined' ? 'unsupported' : Notification.permission,
  );

  if (items.length === 0) return null;

  const taking = takingId ?? null;

  return (
    <section className="flex flex-col gap-3 rounded-2xl border border-slate-200 bg-white p-5">
      <header className="flex items-center justify-between gap-2">
        <h2 className="flex items-center gap-2 text-base font-bold text-slate-900">
          <BellRing className="h-4 w-4 text-blue-600" aria-hidden />
          Перезвоны
          <span className="text-sm font-semibold text-slate-400">{items.length}</span>
        </h2>
        {withSound && notifState === 'default' ? (
          <button
            type="button"
            className="text-sm font-medium text-blue-700 hover:underline"
            onClick={() => {
              void Notification.requestPermission().then(setNotifState);
            }}
          >
            Включить уведомления
          </button>
        ) : null}
      </header>

      <ul className="flex flex-col gap-2">
        {items.map((item) => {
          const dueAt = new Date(item.dueAt);
          // Без назначенного человеком времени (легаси до выбора времени)
          // dueAt — пауза круга: красить «Просрочен» и будить по ней — врать
          const state = item.scheduled ? dueState(dueAt, now) : 'future';
          return (
            <li
              key={item.queueItemId}
              className={
                'flex items-center justify-between gap-3 rounded-xl px-3 py-2.5 text-sm ' +
                rowClasses(state)
              }
            >
              <div className="min-w-0">
                <p className="flex flex-wrap items-baseline gap-x-2">
                  <span className="font-bold tabular-nums">
                    {item.scheduled ? dueLabel(dueAt, state, now) : 'Время не назначалось —'}
                  </span>
                  <span className="font-semibold tabular-nums">{formatPhone(item.phone)}</span>
                  {item.fullName !== null ? (
                    <span className="min-w-0 break-words">{item.fullName}</span>
                  ) : null}
                </p>
                {item.comment !== null ? (
                  <p className="mt-0.5 break-words whitespace-pre-line opacity-80">
                    {item.comment}
                  </p>
                ) : null}
              </div>
              {onTake !== undefined ? (
                <button
                  type="button"
                  disabled={taking !== null}
                  title="Забрать номер карточкой и набрать"
                  onClick={() => {
                    onTake(item);
                  }}
                  className={
                    'inline-flex shrink-0 items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-semibold disabled:opacity-60 ' +
                    takeClasses(state)
                  }
                >
                  <Phone className="h-4 w-4" aria-hidden />
                  {taking === item.queueItemId ? 'Набираю…' : 'Позвонить'}
                </button>
              ) : null}
            </li>
          );
        })}
      </ul>

      {takeError !== undefined ? (
        <p className="rounded-xl bg-red-50 px-3 py-2 text-sm text-red-800">
          Не удалось забрать номер: {takeError}. Обновите страницу — возможно, карточка уже
          на пульте.
        </p>
      ) : null}

      <p className="text-sm text-slate-500">
        {onTake !== undefined
          ? '«Позвонить» забирает номер карточкой и сразу набирает. Не нажмёте — номер придёт сам с ближайшей раздачей, держите «Готова» включённой.'
          : 'В назначенное время номер сам придёт карточкой — держите «Готова» включённой и свободное место на руках.'}
      </p>
    </section>
  );
}

function rowClasses(state: DueState): string {
  if (state === 'overdue') return 'bg-red-50 text-red-800';
  if (state === 'due') return 'bg-amber-50 text-amber-800';
  return 'bg-slate-50 text-slate-700';
}

/** Наступившие — синяя кнопка действия; будущие — тихая, звонить раньше можно, но не зовём */
function takeClasses(state: DueState): string {
  if (state === 'future') return 'border border-slate-200 bg-white text-slate-700 hover:bg-slate-100';
  return 'bg-blue-600 text-white hover:bg-blue-700';
}

/**
 * «15:00», для чужого дня — с датой. У наступивших время остаётся в скобках:
 * по двум красным «Просрочен» без времени не понять, на сколько договаривались
 * и в каком порядке отзванивать (ревью 30.08.2026).
 */
function dueLabel(dueAt: Date, state: DueState, now: Date): string {
  const at = formatWhenMsk(dueAt, now);
  if (state === 'due') return `Пора (${at}):`;
  if (state === 'overdue') return `Просрочен (${at}):`;
  return at;
}

/** Ключ писка: перезвон + его время — новая договорённость пикает заново. */
function alarmKey(item: { queueItemId: string; dueAt: string }): string {
  return `${item.queueItemId}:${item.dueAt}`;
}

/**
 * Двойной тон ~0.4 с. Web Audio запускается только после жеста пользователя —
 * у работающего диспетчера жесты были; если контекст не дали, молчим без ошибки.
 */
function beep(): void {
  try {
    const ctx = new AudioContext();
    // До первого клика по странице контекст рождается приостановленным и молчит;
    // resume после жеста включает его, без жеста — тихо откажет, не упав
    void ctx.resume().catch(() => undefined);
    const gain = ctx.createGain();
    gain.gain.value = 0.08;
    gain.connect(ctx.destination);
    for (const [freq, start] of [
      [880, 0],
      [660, 0.2],
    ] as const) {
      const osc = ctx.createOscillator();
      osc.frequency.value = freq;
      osc.connect(gain);
      osc.start(ctx.currentTime + start);
      osc.stop(ctx.currentTime + start + 0.15);
    }
    setTimeout(() => {
      void ctx.close();
    }, 600);
  } catch {
    // звука нет — остаётся подсветка строки и уведомление
  }
}

/** Браузерное уведомление, когда вкладка не на виду. Разрешения нет — молчим. */
function notify(items: CallbackItem[]): void {
  if (typeof Notification === 'undefined' || Notification.permission !== 'granted') return;
  if (!document.hidden) return;
  const first = items[0];
  if (first === undefined) return;
  try {
    new Notification('Пора перезвонить', {
      body:
        `${formatPhone(first.phone)}${first.fullName !== null ? ` — ${first.fullName}` : ''}` +
        (items.length > 1 ? ` и ещё ${String(items.length - 1)}` : ''),
      tag: 'cc-callback',
    });
  } catch {
    // уведомления запрещены политикой — писк уже прозвучал
  }
}
